import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AuthError from './AuthError';
import EmailPasswordForm from './EmailPasswordForm';
import LoginContainer from './LoginContainer';
import MicrosoftLoginButton from './MicrosoftLoginButton';
import ZoomLoginButton from './ZoomLoginButton';
import { CLASS_LOGO_PATH } from '../../config/apiPaths';
import { TEAMS_EULA } from '../../constants/urls';
import { OrganizationType } from '../../hooks/useSchoolInfo';
import { createOauthState, parseOauthState } from '../../utils/oauthState';
import { getUrlParams } from '../../utils/url';
import MeetingInfo from '../SignUp/MeetingInfo';
import OtherJoinOptions from '../SignUp/OtherJoinOptions';
import SignUpPageLoading from '../SignUp/SignUpPageLoading';

const Container = styled.div`
    display: flex;
    flex-flow: column;
    height: 100vh;
    align-items: center;
`;

const LoginBody = styled.div`
    max-width: 400px;
    margin: 6rem 4rem 4rem 4rem;
`;

export const Title = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
`;

export const TitleText = styled.h1`
    font-size: 18px;
    font-weight: normal;
`;

export const ImageWrapper = styled.div<{ imageUrl: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 175px;
    height: 58px;
    margin: 0 auto 2rem;
    background: transparent url(${(props) => props.imageUrl}) no-repeat center;
    background-size: contain;
`;

const OAuthActions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
`;

const LoginTypeSeparator = styled.div`
    display: flex;
    justify-content: center;
`;

const Divider = styled.div`
    position: relative;
    margin: 2rem 0;
    color: #a7a7a7;

    &:before,
    &:after {
        position: absolute;
        content: '';
        height: 1px;
        width: 30px;
        background-color: #a7a7a7;
        top: 50%;
    }

    &:before {
        right: 24px;
    }

    &:after {
        left: 24px;
    }
`;

const LoginFooter = styled.div`
    margin-top: auto;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    padding: 12px 16px;
    gap: 12px;
`;

interface QueryParams {
    /**
     * Shows the email and password login form
     */
    flag_show_password?: boolean;
    /**
     * Shows the zoom oauth login button
     */
    flag_show_zoom?: boolean;
}

const LoginPage: React.FC = () => {
    const { isLoading, school, isAutoSignIn, isOriginMeeting, meetingData, backToStateUrl } =
        useContainer(LoginContainer);
    const query: QueryParams = getUrlParams();

    const showPasswordFlag = query.flag_show_password;
    const showOauthFlag = query.flag_show_zoom;

    const showPasswordLogin = school?.type !== OrganizationType.User || showPasswordFlag;
    const showOauthLogin = school?.type !== OrganizationType.Collab || showOauthFlag;

    const showZoomButton = school?.type !== OrganizationType.Teams || showOauthFlag;
    const showMicrosoftButton = school?.type === OrganizationType.Teams || showOauthFlag;

    const showEulaLink = school?.type === OrganizationType.Teams;

    const { t } = useTranslation('login');

    if (isAutoSignIn) return null;

    const renderEmailPasswordLogin = () => {
        if (!showPasswordLogin) return null;

        return <EmailPasswordForm />;
    };

    const renderDivider = () => {
        if (showOauthLogin && showPasswordLogin) {
            return (
                <LoginTypeSeparator>
                    <Divider>{t('or')}</Divider>
                </LoginTypeSeparator>
            );
        }
    };

    const renderOauthOptions = () => {
        if (!showOauthLogin) return null;

        return (
            <OAuthActions>
                {showMicrosoftButton && <MicrosoftLoginButton state={parseOauthState() || createOauthState()} />}
                {showZoomButton && <ZoomLoginButton />}
            </OAuthActions>
        );
    };

    if (isLoading) {
        return <SignUpPageLoading />;
    }

    return (
        <Container>
            <LoginBody>
                <ImageWrapper imageUrl={CLASS_LOGO_PATH} />

                <Title>
                    <TitleText>
                        {school?.name ? (
                            <Trans
                                t={t}
                                i18nKey="sign-in-to"
                                values={{ school: school.name }}
                                tOptions={{ interpolation: { escapeValue: true } }}
                                shouldUnescape
                            />
                        ) : (
                            t('sign-in-to-default')
                        )}
                    </TitleText>
                </Title>

                <AuthError />
                {meetingData && <MeetingInfo className={meetingData.className} classTimes={meetingData.classTimes} />}
                {renderEmailPasswordLogin()}
                {renderDivider()}
                {renderOauthOptions()}
            </LoginBody>

            {isOriginMeeting && <OtherJoinOptions backToStateUrl={backToStateUrl} />}

            <LoginFooter>
                {showEulaLink && (
                    <a href={TEAMS_EULA} target="_blank">
                        {t('eula')}
                    </a>
                )}
            </LoginFooter>
        </Container>
    );
};

export default LoginPage;
